import { useMutation } from '@apollo/client';
import type { UpdateLookalikeSegmentInput } from '@openx/types/segments';
import { enqueueSnackbar } from 'notistack';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { CreateLookalikeObjectsDocument } from '../../../graphql/mutation/segments/CreateLookalikeObjects.generated';
import { UpdateLookalikeObjectsDocument } from '../../../graphql/mutation/segments/lal/UpdateLookalikeObjects.generated';
import { NestedErrorCode } from '../../../modules/graphql/types';
import { useLocalErrorHandling } from '../../../modules/graphql/useLocalErrorHandling';

export const useUpdateLookalikeSegment = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const [updateLookalikeObjects, { loading }] = useMutation(UpdateLookalikeObjectsDocument, {
    refetchQueries: ['GetLalSegmentFeed', 'GetLalSegmentsList', 'GetLalSegment'],
  });

  const updateLookalikeSegment = useCallback(
    async (id: string, input: UpdateLookalikeSegmentInput) => {
      const updateLookalikeResponse = await updateLookalikeObjects({
        variables: {
          id,
          definitionSet: input.definition,
          seedSegmentIdsSet: input.seedSegmentIds,
        },
      });

      if (updateLookalikeResponse.data?.updateLookalikeObjectsAndSegments) {
        enqueueSnackbar(
          t('Lookalike segment {segmentName} was successfully updated', {
            segmentName: input.definition.lookalike_name,
          }),
          { variant: 'success' }
        );
      } else {
        enqueueSnackbar(
          t('Lookalike segment {segmentName} not updated; LAL and seed segments unchanged', {
            segmentName: input.definition.lookalike_name,
          }),
          { variant: 'warning' }
        );
      }

      navigate(`/segments/lookalike/${id}`);
    },
    [updateLookalikeObjects, navigate, t]
  );

  const errorCallback = useCallback(
    error => {
      if (error.code !== NestedErrorCode.JOINT_UNIQUENESS_VIOLATION || error.path !== 'objects.lookalike_name')
        return false;

      enqueueSnackbar(t('Lookalike segment with such name already exists'), { variant: 'error' });
      return true;
    },
    [t]
  );

  useLocalErrorHandling(CreateLookalikeObjectsDocument, errorCallback);

  return {
    updateLookalikeSegment,
    isUpdating: loading,
  };
};
